import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Calendar,
  CreditCard,
  User,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Ban,
  FileText,
  Plus,
  Building2,
  Receipt,
  Unplug,
} from "lucide-react";

interface ActivityTabProps {
  activities: any[];
  loading: boolean;
}

export function ActivityTab({ activities, loading }: ActivityTabProps) {
  // Helper function to get display name for a creator.
  // Numeric actor ids are resolved server-side (see the activities API, which
  // uses a service-role client so cross-venue staff/admins resolve correctly)
  // and provided via activity.created_by_name, so the numeric branch here is
  // only a defensive fallback.
  const getCreatorDisplayName = (creator: string | number | null | undefined): string => {
    // Handle null/undefined/empty cases
    if (!creator) {
      return "System";
    }

    // Convert to string for consistent handling
    const creatorStr = creator.toString();

    // If a numeric id slips through unresolved, show a stable fallback.
    if (!isNaN(Number(creatorStr))) {
      return `User ${creatorStr}`;
    }

    // If it's "System" or similar, return as-is
    if (creatorStr === "System" || creatorStr === "Guest (Online)") {
      return creatorStr;
    }

    // If it's a role name, return a friendly display
    const roleDisplayMap: Record<string, string> = {
      'super_admin': 'Admin',
      'booking_staff': 'Staff',
      'bar_staff': 'Bar Staff',
      'customer': 'Customer',
      'Customer': 'Customer', // Handle both cases
      'guest_user': 'Guest',
      'readonly': 'Viewer',
      'host': 'Host',
    };

    if (roleDisplayMap[creatorStr]) {
      return roleDisplayMap[creatorStr];
    }

    // Otherwise return as-is
    return creatorStr;
  };
  const getActivityIcon = (activity: any) => {
    // Parse activity detail to get the type
    let detail;
    try {
      detail =
        typeof activity.activity_detail === "string"
          ? JSON.parse(activity.activity_detail)
          : activity.activity_detail;
    } catch (e) {
      detail = {};
    }

    const type = detail?.type || activity.activity_type;

    switch (type) {
      case "booking_created":
        return <Calendar className="h-4 w-4 text-green-500" />;
      case "status_change":
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case "payment_status_change":
        return <CreditCard className="h-4 w-4 text-purple-500" />;
      case "payment_method_change":
        return <CreditCard className="h-4 w-4 text-blue-500" />;
      case "customer_updated":
      case "client_info_updated":
      case "customer_details_change":
      case "host_change":
        return <User className="h-4 w-4 text-orange-500" />;
      case "booking_cancelled":
        return <Ban className="h-4 w-4 text-red-500" />;
      case "invoice_sent":
        return <FileText className="h-4 w-4 text-blue-500" />;
      case "invoice_customer_created":
        return <Building2 className="h-4 w-4 text-green-500" />;
      case "invoice_generated":
        return <Receipt className="h-4 w-4 text-green-500" />;
      case "invoice_disconnected":
        return <Unplug className="h-4 w-4 text-orange-500" />;
      case "invoice_generation_failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "addon_added":
      case "add_on_added":
      case "addon_updated":
      case "add_on_updated":
      case "addon_removed":
      case "add_on_removed":
      case "shop_item_added":
      case "shop_item_updated":
      case "shop_item_removed":
        return <Plus className="h-4 w-4 text-green-500" />;
      case "show_assignment_removed":
      case "show_assignment_archived":
      case "show_removed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "show_added":
      case "show_assignment_added":
        return <Calendar className="h-4 w-4 text-green-500" />;
      case "participants_updated":
      case "participant_count_change":
        return <User className="h-4 w-4 text-blue-500" />;
      case "price_change":
      case "notes_change":
      case "date_time_change":
      case "date_time_updated":
      case "language_change":
      case "booking_type_change":
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatActivityDescription = (activity: any) => {
    let detail;
    try {
      detail =
        typeof activity.activity_detail === "string"
          ? JSON.parse(activity.activity_detail)
          : activity.activity_detail;
    } catch (e) {
      detail = {};
    }

    // Get the type from activity detail or fall back to activity_type
    const activityType = detail?.type || activity.activity_type;

    // Helper function to format field names for display
    const formatFieldName = (fieldName: string): string => {
      const fieldMap: Record<string, string> = {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",
        phone: "Phone",
        company_name: "Company Name",
        org_number: "Organization Number",
        participant_count: "Participant Count",
        details: "Notes/Details",
        notes: "Notes",
        lang: "Language",
        status: "Status",
        payment_status: "Payment Status",
        payment_method: "Payment Method",
        customer_type_id: "Booking Type",
        partner: "Partner",
        end_customer_name: "End Customer",
      };
      return fieldMap[fieldName] || fieldName.replace(/_/g, " ");
    };

    switch (activityType) {
      case "booking_created":
        return "Booking was created";
      case "status_change":
        return `Booking status changed from ${detail?.old_display || "Unknown"} to ${detail?.new_display || "Unknown"}`;
      case "payment_status_change":
        return `Payment status changed from ${detail?.old_display || "Unknown"} to ${detail?.new_display || "Unknown"}`;
      case "payment_method_change":
        return `Payment method changed from ${detail?.old_display || "Unknown"} to ${detail?.new_display || "Unknown"}`;
      case "customer_updated":
      case "client_info_updated":
        return "Customer information was updated";
      case "customer_details_change":
        const fieldName = formatFieldName(detail?.field || "Unknown");
        return `${fieldName} changed from "${detail?.old_display || detail?.old_value || "Unknown"}" to "${detail?.new_display || detail?.new_value || "Unknown"}"`;
      case "booking_cancelled":
        return `Booking was cancelled: ${detail?.reason || "No reason provided"}`;
      case "host_change":
        return `Host changed from ${detail?.old_display || "Unknown"} to ${detail?.new_display || "Unknown"}`;
      case "addon_added":
      case "add_on_added":
        return `Add-on "${detail?.addon_name || detail?.add_on_name || "Unknown"}" added (${detail?.units || detail?.new_value || 1} units, ${detail?.currency || ""}${detail?.price || 0} each)`;
      case "addon_updated":
      case "add_on_updated":
        const addonChanges = [];
        if (detail?.old_price !== detail?.new_price) {
          addonChanges.push(
            `price: ${detail?.currency || ""}${detail?.old_price || 0} → ${detail?.currency || ""}${detail?.new_price || 0}`,
          );
        }
        if (detail?.old_quantity !== detail?.new_quantity) {
          addonChanges.push(
            `quantity: ${detail?.old_quantity || 0} → ${detail?.new_quantity || 0}`,
          );
        }
        return `Add-on "${detail?.addon_name || detail?.add_on_name || "Unknown"}" updated${addonChanges.length > 0 ? ` (${addonChanges.join(", ")})` : ""}`;
      case "addon_removed":
      case "add_on_removed":
        return `Add-on "${detail?.addon_name || detail?.add_on_name || "Unknown"}" removed${detail?.reason ? ` - Reason: ${detail.reason}` : ""}`;
      case "shop_item_added":
        return `Shop item "${detail?.shop_item_name || "Unknown"}" added (${detail?.units || 1} units, ${detail?.currency || ""}${detail?.price || 0} each)`;
      case "shop_item_updated":
        const shopChanges = [];
        if (detail?.old_price !== detail?.new_price) {
          shopChanges.push(
            `price: ${detail?.currency || ""}${detail?.old_price || 0} → ${detail?.currency || ""}${detail?.new_price || 0}`,
          );
        }
        if (detail?.old_quantity !== detail?.new_quantity) {
          shopChanges.push(
            `quantity: ${detail?.old_quantity || 0} → ${detail?.new_quantity || 0}`,
          );
        }
        return `Shop item "${detail?.shop_item_name || "Unknown"}" updated${shopChanges.length > 0 ? ` (${shopChanges.join(", ")})` : ""}`;
      case "shop_item_removed":
        return `Shop item "${detail?.shop_item_name || "Unknown"}" removed${detail?.reason ? ` - Reason: ${detail.reason}` : ""}`;
      case "participants_updated": // New constant name
      case "participant_count_change": // Legacy name
        return `Participant count changed from ${detail?.old_count || detail?.old_value || "Unknown"} to ${detail?.new_count || detail?.new_value || "Unknown"}`;
      case "price_change":
        return `Price changed from ${detail?.old_display || detail?.old_value || "Unknown"} to ${detail?.new_display || detail?.new_value || "Unknown"}`;
      case "notes_change":
        return `Notes changed from "${detail?.old_display || "(empty)" || "Unknown"}" to "${detail?.new_display || "(empty)" || "Unknown"}"`;
      case "date_time_change":
      case "date_time_updated":
        return `Date/time changed from "${detail?.old_display || "Not set"}" to "${detail?.new_display || "Not set"}"`;
      case "language_change":
        return `Language changed from ${detail?.old_display || detail?.old_value?.toUpperCase() || "Unknown"} to ${detail?.new_display || detail?.new_value?.toUpperCase() || "Unknown"}`;
      case "booking_type_change":
        return `Booking type changed from ${detail?.old_display || "Unknown"} to ${detail?.new_display || "Unknown"}`;
      case "show_added":
      case "show_assignment_added":
        const showName = detail?.show_name || "Unknown show";
        const showTime = detail?.time || "Unknown time";
        const showDate = detail?.date || "Unknown date";
        return `Show "${showName}" added (${showDate} at ${showTime})`;
      case "show_removed":
      case "show_assignment_removed":
      case "show_assignment_archived":
        const removedShowName = detail?.show_name || detail?.old_show || "Unknown show";
        const removedShowTime = detail?.time || "Unknown time";
        const removedShowDate = detail?.date || "Unknown date";
        return `Show "${removedShowName}" removed${removedShowDate ? ` (${removedShowDate}${removedShowTime ? ` at ${removedShowTime}` : ""})` : ""}${detail?.reason ? ` - Reason: ${detail.reason}` : ""}`;
      case "invoice_customer_created":
        const companyInfo = detail?.company_name
          ? ` (${detail.company_name})`
          : "";
        return `Customer created in accounting system: ${detail?.customer_name || "Unknown"}${companyInfo} - ID: ${detail?.customer_id || "Unknown"}`;
      case "invoice_generated":
        return `Invoice #${detail?.invoice_number || "Unknown"} generated - Amount: ${detail?.amount || 0} ${detail?.currency || ""} - Items: ${detail?.items || "Unknown"}`;
      case "invoice_disconnected":
        return `Invoice #${detail?.invoice_number || "Unknown"} disconnected${detail?.reason ? ` - Reason: ${detail.reason}` : ""}`;
      case "invoice_generation_failed":
        return `Invoice generation failed: ${detail?.error_message || "Unknown error"}${detail?.error_code ? ` (Code: ${detail.error_code})` : ""}`;
      default:
        // For simple string activities (legacy format)
        if (typeof activity.activity_detail === "string" && !detail?.type) {
          return activity.activity_detail;
        }
        return activityType
          ? activityType.replace(/_/g, " ")
          : "Unknown activity";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border h-full">
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Loading activities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border h-full">
      <ScrollArea className="h-full p-4">
        <div className="space-y-4">
          {activities.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No activities yet</p>
          ) : (
            activities.map((activity, index) => (
              <div key={activity.id || index} className="flex gap-4">
                {/* Timeline Line */}
                <div className="relative flex flex-col items-center">
                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                    {getActivityIcon(activity)}
                  </div>
                  {index < activities.length - 1 && (
                    <div
                      className="flex-1 w-px bg-gray-200 mt-2"
                      style={{ minHeight: "24px" }}
                    ></div>
                  )}
                </div>

                {/* Activity Content */}
                <div className="flex-1 pb-4">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {formatActivityDescription(activity)}
                </p>
                {(() => {
                  let detail;
                  try {
                    detail =
                      typeof activity.activity_detail === "string"
                        ? JSON.parse(activity.activity_detail)
                        : activity.activity_detail;
                  } catch (e) {
                    detail = {};
                  }
                  return (
                    detail?.reason &&
                    detail.reason !== "Host updated" && (
                      <p className="text-sm text-gray-600 italic mb-2">
                        Reason: {detail.reason}
                      </p>
                    )
                  );
                })()}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <User className="h-3 w-3" />
                    <span>
                      {(() => {
                        let detail;
                        try {
                          detail =
                            typeof activity.activity_detail === "string"
                              ? JSON.parse(activity.activity_detail)
                              : activity.activity_detail;
                        } catch (e) {
                          detail = {};
                        }
                        // Prefer the server-resolved name (created_by_name),
                        // which already maps numeric actor ids to real names.
                        const creator =
                          activity.created_by_name ||
                          detail?.changed_by ||
                          detail?.updated_by ||
                          detail?.added_by ||
                          detail?.removed_by ||
                          "System";

                        // Use the helper function to get display name
                        return getCreatorDisplayName(creator);
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>
                      {new Date(activity.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </ScrollArea>
    </div>
  );
}
